import React, { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, ChevronRight } from "lucide-react";

export default function LabPatients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/lab/patients").then((r) => setPatients(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const filtered = useMemo(() => !q ? patients : patients.filter((p) => (p.patient_name || "").toLowerCase().includes(q.toLowerCase())), [patients, q]);
  return (
    <DashboardLayout role="lab_admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink">Patients</h1>
          <p className="text-ink/60">{patients.length} active patients across all branches</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patients…" className="pl-9 h-10 w-64" data-testid="patients-search" />
        </div>
      </div>
      <Card className="mt-6 rounded-2xl overflow-hidden">
        <div className="hidden lg:grid grid-cols-[2fr_0.8fr_0.6fr_1fr_0.7fr_0.5fr] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40">
          <div>Patient</div><div>Age / Gender</div><div>Reports</div><div>Last visit</div><div>Status</div><div />
        </div>
        {loading && (
          <div className="p-6 space-y-3">
            {[0,1,2,3].map((i) => (<div key={i} className="h-14 rounded-xl bg-muted/40 animate-pulse" />))}
          </div>
        )}
        {!loading && filtered.length === 0 && <div className="p-10 text-center text-ink/50">No patients yet.</div>}
        {!loading && filtered.map((p) => {
          const initials = (p.patient_name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
          const flagged = p.flagged > 0;
          return (
            <div key={p.patient_id} data-testid={`patient-${p.patient_id}`} className="border-b border-border hover:bg-muted/40 transition-all">
              {/* Desktop row */}
              <div className="hidden lg:grid grid-cols-[2fr_0.8fr_0.6fr_1fr_0.7fr_0.5fr] gap-3 px-5 py-3 items-center">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-ink text-white grid place-items-center text-xs font-heading font-bold">{initials || "P"}</div>
                  <div className="font-medium text-ink">{p.patient_name}</div>
                </div>
                <div className="text-sm text-ink/70">{p.patient_age ? `${p.patient_age} / ${p.patient_gender || "—"}` : "—"}</div>
                <div className="text-sm text-ink/70">{p.reports}</div>
                <div className="text-sm text-ink/60">{p.last_visit ? new Date(p.last_visit).toLocaleDateString() : "—"}</div>
                <div><span className={`pill ${flagged ? "pill-high" : "pill-normal"}`}>{flagged ? "Needs follow-up" : "Active"}</span></div>
                <div className="text-right"><ChevronRight className="w-4 h-4 text-ink/30 inline" /></div>
              </div>
              {/* Mobile card */}
              <div className="lg:hidden flex items-start gap-3 px-4 py-3">
                <div className="w-11 h-11 rounded-lg bg-ink text-white grid place-items-center text-sm font-heading font-bold shrink-0">{initials || "P"}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-ink truncate">{p.patient_name}</div>
                    <span className={`pill ${flagged ? "pill-high" : "pill-normal"} shrink-0 text-[10px]`}>{flagged ? "Follow-up" : "Active"}</span>
                  </div>
                  <div className="text-xs text-ink/50 mt-0.5">{p.patient_age ? `${p.patient_age}y · ${p.patient_gender || "—"}` : "—"}</div>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-ink/60">
                    <span><b className="text-ink">{p.reports}</b> reports</span>
                    <span>Last visit {p.last_visit ? new Date(p.last_visit).toLocaleDateString() : "—"}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-ink/30 mt-1 shrink-0" />
              </div>
            </div>
          );
        })}
      </Card>
    </DashboardLayout>
  );
}
